import { z } from 'zod';

/**
 * Validates the P >= 2V rule (on-site slots must be at least 2× remote slots)
 * Used for both SA (Sans Assistant) and AA (Avec Assistant) configurations
 */
export const validateRhythmRule = (
  onSite: number | null | undefined,
  remote: number | null | undefined,
): boolean => {
  // If both are null/undefined, skip validation
  if (
    (onSite === null || onSite === undefined) &&
    (remote === null || remote === undefined)
  ) {
    return true;
  }
  // If only remote is set, on-site must be at least 2× remote
  if (remote !== null && remote !== undefined && remote > 0) {
    const onSiteValue = onSite ?? 0;
    return onSiteValue >= 2 * remote;
  }
  return true;
};

/**
 * Preprocessor that safely converts input to number or null
 * Handles empty strings, null, undefined, and invalid numbers
 */
const toNumberOrNull = (val: unknown): number | null => {
  if (val === '' || val === null || val === undefined) return null;
  const num = Number(val);
  return Number.isNaN(num) ? null : num;
};

/**
 * Creates an optional rhythm slot field schema
 * - Empty fields are valid (submit as null)
 * - When provided, must be a non-negative integer >= 0
 */
const createRhythmSlotSchema = () =>
  z.preprocess(toNumberOrNull, z.number().int().min(0).nullable());

/**
 * Reusable rhythm schema fields for substitute doctor forms
 * These fields can be spread into any Zod object schema
 */
export const rhythmSchemaFields = {
  onSiteSlotsWithoutAssistant: createRhythmSlotSchema(),
  remoteSlotsWithoutAssistant: createRhythmSlotSchema(),
  onSiteSlotsWithAssistant: createRhythmSlotSchema(),
  remoteSlotsWithAssistant: createRhythmSlotSchema(),
  rhythmNote: z.string().nullable().optional(),
};

/**
 * Adds P >= 2V validation refinements to a schema
 * Use this after defining your base schema to add rhythm validation
 *
 * @example
 * const mySchema = addRhythmRefinements(
 *   z.object({
 *     ...rhythmSchemaFields,
 *     // other fields
 *   })
 * );
 */
export const addRhythmRefinements = <T extends z.ZodTypeAny>(
  schema: T,
  options?: { skipValidation?: boolean },
) => {
  return schema
    .refine(
      (data: any) => {
        if (options?.skipValidation) return true;
        // Validate P >= 2V for Sans Assistant (SA)
        return validateRhythmRule(
          data.onSiteSlotsWithoutAssistant,
          data.remoteSlotsWithoutAssistant,
        );
      },
      {
        message: 'rhythm-structured.validation-error',
        path: ['onSiteSlotsWithoutAssistant'],
      },
    )
    .refine(
      (data: any) => {
        if (options?.skipValidation) return true;
        // Validate P >= 2V for Avec Assistant (AA)
        return validateRhythmRule(
          data.onSiteSlotsWithAssistant,
          data.remoteSlotsWithAssistant,
        );
      },
      {
        message: 'rhythm-structured.validation-error',
        path: ['onSiteSlotsWithAssistant'],
      },
    );
};

export type RhythmFields = {
  onSiteSlotsWithoutAssistant?: number | null;
  remoteSlotsWithoutAssistant?: number | null;
  onSiteSlotsWithAssistant?: number | null;
  remoteSlotsWithAssistant?: number | null;
  rhythmNote?: string | null;
};
